import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 2;
const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
const LAST_PAGE = false;
const BG_ID = 2;
const BG_TYPE = "jpg";

const LEFT_TITLE = "Elevated Operational Efficiency\nleveraging AI-First Automation";
const LEFT_TEXT =
  "Transition from reactive and manual intensive\noperations to proactive, predictive, and highly\nautomated operations leveraging native artificial\nintelligence (AI) and machine learning (ML) capabilities";

// Image slider component
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]); // Add currentPage as dependency

  const images = [1, 2, 3];
  const GAP = "calc(var(--width-95) * -1)";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        bottom: "15%", // Position above buttons
        left: "2%", // Start from left side
        width: "70%", // Cover most of the width
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginRight: i < images.length - 1 ? GAP : 0,
            width: "50%", // Adjust width for each image
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "-100%"})`, // Changed to come from left
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              animation: `glow 3s ease-in-out infinite`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  // Define keyframes for animations
  const keyframes = `
    @keyframes gradientMove {
      0% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.03);
      }
      100% {
        transform: scale(1);
      }
    }
    @keyframes shadowPulse {
      0% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3)
      }
      50% {
        box-shadow: 0 0 55px rgba(255,255,255,0.4);
      }
      100% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3);
      }
    }
    @keyframes glow {
      0% { filter: drop-shadow(0 0 1px rgba(125,208,255,0.1)); }
      50% { filter: drop-shadow(0 0 10px rgba(125,208,255,0.5)); }
      100% { filter: drop-shadow(0 0 1px rgba(125,208,255,0.1)); }
    }
  `;

  const leftAfterDescription = (
    <div style={{ padding: "var(--width-10) 0" }}>
      <style>{keyframes}</style>
      <img
        src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/left_separator.png`}
        alt="separator"
        style={{
          width: "var(--width-565)",
          height: "auto",
        }}
      />
      <div
        style={{
          color: "#fff",
          fontFamily: "Myriad Pro",
          fontSize: "var(--width-24)",
          fontStyle: "normal",
          fontWeight: "380",
          lineHeight: "150%",
          whiteSpace: "pre-line",
        }}
      >
        <p
          style={{
            marginTop: "var(--width-30)",
            marginBottom: "var(--width-10)",
          }}
        >
          {LEFT_TEXT}
        </p>
      </div>
    </div>
  );
  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
          style: {
            borderRadius: "45px",
            padding: "var(--width-5) var(--width-25)",
            gap: "var(--width-16)",
          },
        }
      : undefined;

  const nextButton = !LAST_PAGE
    ? {
        label: "Next",
        type: "next" as const,
        endIcon: { icon: "arrow-left" },
        onClick: () => setCurrentPage(NEXT_PAGE_ID),
        style: {
          borderRadius: "45px",
          padding: "var(--width-5) var(--width-25)",
          gap: "var(--width-16)",
        },
      }
    : undefined;

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${PAGE_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",

    title: LEFT_TITLE,
    leftAfterDescription: leftAfterDescription,
    // Force remount of ImageSlider on currentPage change by using key={currentPage}
    rightSideComponent: <ImageSlider key={currentPage} />,
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
